/**
 * 外设服务访问体检 — 区分"服务没启动"和"浏览器拦截本地网络访问"。
 *
 * 背景 [2026-09-09]: 部分客户用 Chrome/Edge 网页版，外设服务明明已启动，
 * 页面却一直提示"外设服务不可达/未启动"。原因是新版 Chromium (内核 138+) 逐步强制
 * Local Network Access 权限：公网 HTTPS 页面首次请求 127.0.0.1 会弹授权框，
 * 用户点了"不允许"后，之后所有本地请求在浏览器权限层直接被拦
 * （console 报 "Permission was denied for this request to access the loopback address space"），
 * 前端 fetch 只能看到笼统的 Failed to fetch，和"服务真没启动"无法区分 —— 本模块专门拆这个。
 *
 * 判定手段:
 *   1. no-cors fetch 探 8765 —— 通了就是通（服务在且权限没问题）；拒绝=连不上或被拦
 *   2. navigator.permissions.query({name:'local-network-access'}) —— 浏览器支持时能拿到
 *      granted / denied / prompt，denied 即实锤浏览器拦截
 * 两者结合给出 verdict + 修复指引（含浏览器站点设置页地址）。
 */

const SERVICE_BASE = 'http://127.0.0.1:8765'

/** 探测外设服务是否可达（no-cors：只要 HTTP 有响应就算通，不受 CORS 影响）。不抛异常。 */
export async function probeServiceReachable(timeoutMs = 3500) {
  try {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    const timer = controller ? setTimeout(() => { try { controller.abort() } catch (e) { /* ignore */ } }, timeoutMs) : null
    try {
      await fetch(SERVICE_BASE + '/api/health', { method: 'GET', mode: 'no-cors', cache: 'no-store', ...(controller ? { signal: controller.signal } : {}) })
      return true
    } finally {
      if (timer) clearTimeout(timer)
    }
  } catch (e) {
    return false
  }
}

/**
 * 查询"本地网络访问"权限状态。浏览器不支持该权限名时返回 'unsupported'。
 * @returns {'granted'|'denied'|'prompt'|'unsupported'|'unknown'}
 */
export async function queryLocalNetworkPermission() {
  try {
    if (typeof navigator === 'undefined' || !navigator.permissions || !navigator.permissions.query) {
      return 'unsupported'
    }
    const st = await navigator.permissions.query({ name: 'local-network-access' })
    return st && st.state ? st.state : 'unknown'
  } catch (e) {
    return 'unsupported' // 老内核/不认识的权限名直接 throw
  }
}

/** 是否 Edge（站点设置页协议是 edge:// 而不是 chrome://） */
function isEdgeBrowser() {
  try {
    return /Edg\//i.test(navigator.userAgent || '')
  } catch (e) {
    return false
  }
}

/**
 * 浏览器站点设置页地址（把"本地网络/设备上的应用"改成允许的地方）。
 * 用当前页面 origin —— 授权是按发起页的站点记的，dev(localhost:3100)/生产(hgc.liangyiagri.com)各自对应。
 */
export function localNetworkSettingsUrl() {
  let origin = 'https://hgc.liangyiagri.com'
  try {
    if (window.location && window.location.origin) origin = window.location.origin
  } catch (e) { /* 用默认 */ }
  const proto = isEdgeBrowser() ? 'edge' : 'chrome'
  return proto + '://settings/content/siteDetails?site=' + encodeURIComponent(origin)
}

/** 是否跑在好粮宝桌面版 (Electron) 里 —— 不受浏览器本地网络权限影响 */
function isElectron() {
  try {
    return /Electron/i.test(navigator.userAgent || '')
  } catch (e) {
    return false
  }
}

// ===== [2026-10-05] "不可达"统一引导弹窗 =====
// 全项目 7 处 8765 不可达报错都经过 peripheralAccessHint() 这一个漏斗（scaleClient /
// jinglunReader / go2rtc / networkDiagnose / peripheralDebugApi），在这里统一触发全局
// 排查弹窗（PeripheralAccessHelperModal，挂在 MainLayout）。
// 网页版真正的修复指引带一长串 edge:// 设置页地址 —— 塞在红条 toast 里用户既看不见也
// 复制不了；浏览器安全策略又禁止网页直接跳转 edge:// / chrome://（window.open 返回
// null、a 标签点击无效、location 赋值静默失败，2026-10-05 实测），所以弹窗里用
// 「一键复制地址 + 粘贴到地址栏」的引导，toast 只留一句短文案。
// util 层不能 import 组件：弹窗组件挂载时通过 registerPeripheralHelperOpener 注册回调。
let _helperOpener = null
let _helperLastShownAt = 0
// [2026-10-15] 在途的"延迟复探"定时器（防瞬时不可达误弹，见 openPeripheralAccessHelper）
let _helperDeferredTimer = null

/** 弹窗组件（MainLayout）挂载时注册打开回调；卸载时传 null 注销 */
export function registerPeripheralHelperOpener(fn) {
  _helperOpener = typeof fn === 'function' ? fn : null
}

/**
 * 打开全局排查弹窗。
 * [2026-10-11] force=true 手动入口（顶栏"外设服务离线"红徽标点击），永远可开不受节流；
 * 自动触发（各通道 catch 到不可达）维持 30 秒节流，且服务离线期间已有常驻红徽标做
 * 稳态提示 —— 同一次离线只自动弹一次，不再每 30s 重复轰炸（横幅带入口可手动重开）。
 * [2026-10-15] 自动触发加"瞬时窗口"防误弹：开机登录后的 0~2 秒里浏览器恢复的页面
 * 常先于外设服务绑上端口（海棠留香案），首个探测扑空属正常竞速而非故障——改为
 * 3 秒后复探一次，恢复了就吞掉不弹；仍不可达才真弹。真故障只晚 3 秒出现，可接受。
 * 返回弹窗是否可用（注册过即视为可用；没注册说明 MainLayout 未挂载，
 * 调用方回退到老长文案兜底）。
 */
export function openPeripheralAccessHelper(force) {
  const registered = !!_helperOpener
  const now = Date.now()
  // 离线episode内已经弹过（_helperLastShownAt 落在离线起点之后）→ 横幅在场，不再自动弹
  const suppressedByOfflineBanner = !force && _offlineSince > 0 && _helperLastShownAt >= _offlineSince
  if (!registered || suppressedByOfflineBanner) return registered
  if (force) {
    _helperLastShownAt = now
    try { _helperOpener() } catch (e) { /* 打不开也不能影响原报错流程 */ }
    return registered
  }
  if (now - _helperLastShownAt < 30000) return registered // 节流期内连复探都不排
  if (_helperDeferredTimer) return registered // 已有一次复探在途
  _helperDeferredTimer = setTimeout(async () => {
    _helperDeferredTimer = null
    let recovered = false
    try { recovered = await probeServiceReachable(2500) } catch (e) { /* 按不可达处理 */ }
    if (recovered) return // 瞬时窗口已过（登录竞速自愈），不弹
    if (Date.now() - _helperLastShownAt < 30000) return // 复探期间已有别的通道弹过
    _helperLastShownAt = Date.now()
    try { _helperOpener() } catch (e) { /* 打不开也不能影响原报错流程 */ }
  }, 3000)
  return registered
}

/** 短版组合提示 —— 各处 catch 到"8765 不可达"时统一替换/追加的文案 */
export function peripheralAccessHint() {
  // 长文案（含设置页地址）挪进引导弹窗；弹窗没挂上时回退老长文案，保证指引不丢
  const helperAvailable = openPeripheralAccessHelper()
  if (isElectron()) {
    // [2026-09-18] 桌面版不受浏览器权限影响：重启好粮宝自动拉起；
    // 服务僵死（进程在、端口占着、HTTP 不应答、托盘右键点不开）时用一键强制重启
    return '外设服务(127.0.0.1:8765)不可达：请重启好粮宝自动拉起外设服务；' +
      '仍不可达可在「设备管理 → 过磅设备配置」页点「重启外设服务」强制恢复' +
      '（托盘右键点不开说明进程僵死，同样用这个按钮）'
  }
  if (helperAvailable) {
    return '外设服务(127.0.0.1:8765)不可达：请先启动好粮宝；已启动仍不可达是浏览器拦了「本地网络」权限 —— 处理步骤见弹窗指引'
  }
  return '外设服务(127.0.0.1:8765)不可达：请先检查外设服务是否已启动（启动好粮宝即可）；' +
    '若已启动仍提示不可达，是浏览器把「本地网络」权限拦了 —— 请打开 ' + localNetworkSettingsUrl() +
    ' 把「本地网络」「设备上的应用」设为允许后刷新页面'
}

/**
 * 完整体检。永远不抛异常。
 * @returns {Promise<{serviceReachable: boolean, permission: string, verdict: string,
 *   title: string, lines: string[], settingsUrl: string}>}
 *   verdict: 'ok' 服务在线且权限正常 / 'service_down' 权限正常但服务连不上 /
 *            'browser_blocked' 实锤浏览器拦截 / 'ambiguous' 无法确定（给组合排查指引）
 */
export async function diagnosePeripheralAccess() {
  const serviceReachable = await probeServiceReachable()
  // 桌面版(Electron)没有浏览器本地网络权限这回事, 服务连不上就是没启动
  const permission = serviceReachable ? 'granted' : (isElectron() ? 'granted' : await queryLocalNetworkPermission())
  const settingsUrl = localNetworkSettingsUrl()

  let verdict = 'ambiguous'
  let title = ''
  let lines = []

  if (serviceReachable) {
    verdict = 'ok'
    title = '外设服务在线，访问正常'
    lines = ['127.0.0.1:8765 连通正常，读卡 / 地磅 / 监控取流都可以直接用。']
  } else if (permission === 'denied') {
    verdict = 'browser_blocked'
    title = '浏览器拦截了「本地网络访问」权限（服务可能没坏）'
    lines = [
      '本页对 127.0.0.1:8765 的请求在浏览器权限层就被拦了（「本地网络访问」被记成了"阻止"）——请求根本没发到外设服务。',
      '修复：把下面这个地址复制到浏览器地址栏打开，找到「本地网络」和「设备上的应用」，改成「允许」，然后刷新本页。',
      '如果列表里没有这两项：确认浏览器（Chrome/Edge）已升级到较新版本；企业电脑可在 chrome://policy 查是否被策略管控；360/QQ 等套壳浏览器入口不同，建议直接换 Chrome。'
    ]
  } else if (permission === 'granted') {
    verdict = 'service_down'
    title = '浏览器权限正常，外设服务没连上（大概率没启动）'
    lines = [
      '「本地网络访问」权限是允许的，问题出在服务本身：127.0.0.1:8765 连不上。',
      '请启动好粮宝桌面版（外设服务会随好粮宝自动拉起），或在系统托盘右键「烘干厂外设服务」启动，然后点「重新体检」。'
    ]
  } else {
    verdict = 'ambiguous'
    title = '无法确定原因，请按顺序排查（1 分钟）'
    lines = [
      '① 确认外设服务已启动：启动好粮宝桌面版，或看系统托盘有没有「烘干厂外设服务」图标。',
      '② 已启动仍提示不可达 → 浏览器多半把「本地网络」权限拦了（之前弹授权框时点过"不允许"）。',
      '③ 把下面地址复制到浏览器地址栏打开，将「本地网络」「设备上的应用」改为「允许」，刷新页面后重新体检。',
      '（Edge 把地址开头的 chrome:// 换成 edge:// 即可；360/QQ 等套壳浏览器建议直接换 Chrome。）'
    ]
  }

  return { serviceReachable, permission, verdict, title, lines, settingsUrl }
}

// ===== [2026-10-11] 全局外设服务在线状态轮询 =====
// 背景: 客户机死机/外设服务被杀后, 前端一切检测都是"出错时被动触发"——地磅读数条更是
// 冻结在最后一帧、亮着"已连"、还能点"使用此读数"把旧重量录进单据（weighbridgeMixin
// 新鲜度看门狗对服务离线显式跳过）。这里给 MainLayout 一个主动探活器: 周期探测
// /api/health 写 store.deviceStatus.serviceOnline, 离线边沿顺手把双磅 connected 斩断,
// 让顶栏红徽标 + 地磅卡片 + "使用此读数"按钮 (都按 connected 门控) 同步变诚实。
let _watchTimer = null
let _watchStore = null
let _offlineSince = 0 // 本次离线episode起点(ms), 0=当前在线/未知; 供弹窗抑制判断

/** 启动全局探活（MainLayout mounted 调用; 幂等）。默认 20s 一探, no-cors 探不通 ≈ 立即失败, 弱机无压力。 */
export function startPeripheralServiceWatch(store, intervalMs = 20000) {
  if (_watchTimer || !store) return
  _watchStore = store
  const tick = async () => {
    try {
      // 本机无外设硬件的机器不探测 (顶栏地磅条整体隐藏, 探了也没人看)
      if (_watchStore.state.deviceStatus.hardwareDisabled) return
      const online = await probeServiceReachable(2500)
      _applyServiceOnline(online)
    } catch (e) { /* 探测自身不能抛 */ }
  }
  tick() // 启动立即探一次, 别等第一个整周期
  _watchTimer = setInterval(tick, intervalMs)
}

/** 停止探活（MainLayout beforeDestroy 调用） */
export function stopPeripheralServiceWatch() {
  if (_watchTimer) { clearInterval(_watchTimer); _watchTimer = null }
  _watchStore = null
}

/** 当前是否处于"已知离线"状态（serviceOnline === false） */
export function isPeripheralServiceOffline() {
  return !!(_watchStore && _watchStore.state.deviceStatus.serviceOnline === false)
}

/** 状态边沿处理: 写 store; 离线边沿斩断双磅 connected (防冻结读数被录单)。 */
function _applyServiceOnline(online) {
  const store = _watchStore
  if (!store) return
  const prev = store.state.deviceStatus.serviceOnline
  if (prev === online) return
  if (online === false) {
    _offlineSince = Date.now()
    // 服务进程都没了, 串口连接必然不存在——"已连+冻结读数"是最危险的假象。
    // 恢复侧不用这里管: 服务复活时 WS 自动重连 + 数据门禁 (读到有效读数才亮已连接) 会重建。
    store.commit('SET_SCALE_CONNECTED', { scale: 'entryScale', connected: false })
    store.commit('SET_SCALE_CONNECTED', { scale: 'exitScale', connected: false })
  } else {
    _offlineSince = 0
  }
  store.commit('SET_PERIPHERAL_SERVICE_ONLINE', online)
}
